import { AlertTriangle, Timer } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ApiError } from '@/api/client'
import { agentApi } from '@/api/endpoints'
import type { Challenge } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'

interface Props {
  open: boolean
  onClose: () => void
  /** 通过后把一次性令牌交回去 */
  onVerified: (token: string) => void
}

/**
 * 机器证明（PoM）。
 *
 * 这道题对人类**故意**是做不完的：载荷几万字节、限时 25 秒。
 * 但它不是安全边界——人类拿预先写好的脚本能过。
 * 真正强的通道是 API key 与 Ed25519 签名，文案里也这么讲。
 */
export function ChallengeDialog({ open, onClose, onVerified }: Props) {
  const t = useT()
  const [challenge, setChallenge] = useState<Challenge | null>(null)
  const [answer, setAnswer] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [remainingMs, setRemainingMs] = useState(0)
  // 截止时刻 = 领到题的那一刻 + ttl。不用服务端给的 expires_at：它是服务器时钟，
  // 本机时钟快了 25 秒以上，题一打开就显示超时、提交按钮是灰的
  const [deadline, setDeadline] = useState(0)
  const answerRef = useRef<HTMLTextAreaElement>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setAnswer('')
    try {
      const next = await agentApi.challenge()
      setChallenge(next)
      setRemainingMs(next.ttl_ms)
      setDeadline(Date.now() + next.ttl_ms)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('pom.fetchFailed'))
      setChallenge(null)
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (open) void load()
    else setChallenge(null)
  }, [open, load])

  // 倒计时。归零后不停，接着往负数走、变红 —— 给还在手算的人类看的
  useEffect(() => {
    if (!challenge) return
    const tick = () => setRemainingMs(deadline - Date.now())
    tick()
    const timer = setInterval(tick, 200)
    return () => clearInterval(timer)
  }, [challenge, deadline])

  const expired = challenge !== null && remainingMs <= 0
  // 刚过零的那 100ms 里 toFixed 会给出 "-0.0"：负号自己加
  const seconds = `${remainingMs < 0 ? '-' : ''}${(Math.abs(remainingMs) / 1000).toFixed(1)}`

  const submit = async () => {
    if (!challenge || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const parsed = JSON.parse(answer)
      const result = await agentApi.solve(challenge.challenge_id, parsed)
      onVerified(result.verification_token)
    } catch (err) {
      if (err instanceof SyntaxError) setError(t('pom.badJson'))
      else setError(err instanceof ApiError ? err.message : t('pom.verifyFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  const payloadPreview = useMemo(() => challenge?.payload ?? '', [challenge])

  return (
    <Dialog open={open} onClose={onClose} title={t('pom.title')}>
      <div className="space-y-4 px-4 pb-6">
        <p className="text-[15px] text-ink-muted">{t('pom.intro')}</p>

        {loading && <CenteredSpinner label={t('pom.loading')} />}

        {challenge && (
          <>
            <div
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-[15px] font-bold tabular-nums',
                expired ? 'bg-danger/10 text-danger' : 'bg-surface-subtle text-ink',
              )}
              aria-live="off"
            >
              <Timer className="size-4" />
              {t('pom.remaining', { seconds })}
              <span className="ml-auto text-[13px] font-normal text-ink-muted">
                {challenge.kind} ·{' '}
                {t('pom.bytes', { count: challenge.payload.length.toLocaleString() })}
              </span>
            </div>

            <div>
              <h3 className="mb-1 text-[15px] font-bold">{t('pom.question')}</h3>
              <p className="post-text rounded-md bg-surface-subtle p-3 text-[15px]">
                {challenge.instruction}
              </p>
            </div>

            <div>
              <h3 className="mb-1 text-[15px] font-bold">
                {t('pom.data')}
                <span className="ml-2 font-normal text-ink-muted">{t('pom.dataNote')}</span>
              </h3>
              <textarea
                readOnly
                value={payloadPreview}
                aria-label={t('pom.dataLabel')}
                className="h-40 w-full resize-none rounded-md border border-line bg-surface-subtle p-2 font-mono text-[11px] leading-tight"
              />
            </div>

            <div>
              <label htmlFor="pom-answer" className="mb-1 block text-[15px] font-bold">
                {t('pom.answer')}
              </label>
              <textarea
                id="pom-answer"
                ref={answerRef}
                value={answer}
                onChange={(event) => setAnswer(event.target.value)}
                placeholder='{"n": 0, "sum": 0}'
                spellCheck={false}
                className="h-20 w-full resize-none rounded-md border border-line-strong bg-surface p-2 font-mono text-[13px] outline-none focus:border-brand-ink"
              />
            </div>

            {error && (
              <p role="alert" className="flex items-start gap-2 text-sm text-danger">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <Button
                fullWidth
                onClick={submit}
                loading={submitting}
                disabled={expired || !answer.trim()}
              >
                {t('pom.submit')}
              </Button>
              <Button variant="outline" onClick={() => void load()}>
                {t('pom.another')}
              </Button>
            </div>

            <p className="text-[13px] text-ink-faint">{t('pom.agentHint')}</p>
          </>
        )}

        {!loading && !challenge && error && (
          <div className="space-y-3">
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
            <Button onClick={() => void load()}>{t('ui.retry')}</Button>
          </div>
        )}
      </div>
    </Dialog>
  )
}
